<template>
  <li
    class="wallet-card"
    :class="{ playing: playState !== 'off', auto: playState === 'auto' }"
    tabindex="0"
    :aria-label="`Open ${displayName} settings`"
    @click="emit('settings', walletKey)"
    @keydown.enter.prevent="emit('settings', walletKey)"
  >
    <span class="play-indicator" :class="playState" :title="playStateLabel" aria-hidden="true"></span>

    <div class="wallet-main">
      <strong class="wallet-name">{{ displayName }}</strong>
      <button class="wallet-key" type="button" :title="'Copy: ' + walletKey" @click.stop="copy">{{ displayKey }}</button>
      <div class="wallet-metrics">
        <b>{{ balanceText }}</b>
        <span v-if="pnlLamports !== 0" class="wallet-pnl" :class="{ positive: pnlLamports > 0, negative: pnlLamports < 0 }">P: {{ pnlText }}</span>
        <span v-if="tokenAmount > 0" class="token-amount">T: {{ formatAmountHuman(tokenAmount) }}</span>
        <span v-if="copied" class="copied">copied</span>
      </div>
    </div>

    <IconAction class="send-action" name="send" label="Transfer" :size="30" @click.stop="emit('transfer', walletKey)" />
  </li>
</template>

<script setup>
import { computed, ref } from 'vue'
import IconAction from '@/components/_shared/IconAction.vue'
import { tryCopy } from '@/composables/clipboard'
import { shortKey, formatLamports, formatAmountHuman } from '@/composables/format'
import { formatPnlWithPct } from '@/components/_shared/format'

const props = defineProps({
  wallet: { type: Object, required: true },
  tokenAmount: { type: Number, default: 0 },
  playState: { type: String, default: 'off', validator: value => ['off', 'live', 'auto'].includes(value) },
})
const emit = defineEmits(['settings', 'transfer'])
const copied = ref(false)
let copiedTimer = 0

const walletKey = computed(() => String(props.wallet?.public_key || props.wallet?.publicKey || props.wallet?.PublicKey || '').trim())
const walletBalance = computed(() => props.wallet?.lamports ?? props.wallet?.Lamports ?? props.wallet?.balance ?? props.wallet?.Balance ?? 0)
const displayName = computed(() => String(props.wallet?.name || props.wallet?.Name || '').trim() || shortKey(walletKey.value, 5, 5, 16) || 'Wallet')
const displayKey = computed(() => shortKey(walletKey.value, 5, 5, 16) || 'No key')
const balanceText = computed(() => formatLamports(walletBalance.value))
const pnlLamports = computed(() => Number(props.wallet?.pnl_day ?? props.wallet?.pnlDay ?? props.wallet?.PnLDay ?? 0))
const pnlBasisLamports = computed(() => Number(props.wallet?.pnl_day_cost ?? props.wallet?.pnlDayCost ?? 0))
const pnlText = computed(() => formatPnlWithPct(pnlLamports.value, pnlBasisLamports.value))
const playStateLabel = computed(() => props.playState === 'auto' ? 'AUTO' : props.playState === 'live' ? 'LIVE' : 'Not in Play')

async function copy() {
  if (!walletKey.value || !(await tryCopy(walletKey.value))) return
  copied.value = true
  if (copiedTimer) clearTimeout(copiedTimer)
  copiedTimer = setTimeout(() => { copied.value = false }, 900)
}
</script>

<style scoped>
.wallet-card{display:grid;grid-template-columns:10px minmax(0,1fr) 30px;align-items:center;gap:10px;min-width:0;min-height:86px;padding:10px;border:1px solid #222;border-radius:10px;background:#0f0f0f;color:#e5e7eb;cursor:pointer;outline:none;transition:border-color .15s ease,background .15s ease,box-shadow .15s ease}.wallet-card:hover,.wallet-card:focus-visible{border-color:#415166;background:#13171d}.wallet-card.playing{border-color:#245b3b;box-shadow:inset 0 0 0 1px #245b3b33}.wallet-card.auto{box-shadow:inset 0 0 0 1px #245b3b55,0 0 14px #22c55e16}
.play-indicator{width:7px;height:7px;border-radius:999px;background:#3b4148;box-shadow:none}.play-indicator.live{background:#22c55e;box-shadow:0 0 8px #22c55e88}.play-indicator.auto{background:#39ff88;box-shadow:0 0 10px #39ff88;animation:auto-pulse 1.25s ease-in-out infinite}@keyframes auto-pulse{50%{opacity:.55;transform:scale(.72);box-shadow:0 0 4px #39ff8877}}
.wallet-main{min-width:0;display:grid;gap:4px}.wallet-name{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#fff;font-size:14px;font-weight:850}.wallet-key{display:block;min-width:0;max-width:100%;padding:0;border:0;background:transparent;color:#8fa1b5;font:inherit;font-size:11px;text-align:left;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;cursor:pointer}.wallet-key:hover{color:#dbeafe}.wallet-metrics{min-width:0;display:flex;align-items:center;gap:8px;overflow:hidden;color:#94a3b8;font-size:10px;white-space:nowrap}.wallet-metrics b{color:#22c55e;font-size:11px}.wallet-pnl{font-weight:800}.wallet-pnl.positive{color:#22c55e}.wallet-pnl.negative{color:#f87171}.token-amount{color:#86efac}.copied{color:#22c55e}.send-action{justify-self:end}
</style>
